import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { getTask, regenerateTask, retryTask } from '@/api/tasks';
import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { FAILURE_STATUSES } from '@/types/api';
import type { Stage } from '@/types/api';
import { formatDateTime, formatDuration } from '@/lib/format';

function CopyButton({ payload }: { payload: unknown }) {
  const { t } = useTranslation('tasks');
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-6 px-2 text-xs"
      onClick={() => {
        void navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2_000);
      }}
    >
      {copied ? t('detail.copied') : t('detail.copyJson')}
    </Button>
  );
}

export default function TaskDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { t } = useTranslation(['tasks', 'errors']);
  const queryClient = useQueryClient();

  const taskQuery = useQuery({
    queryKey: ['tasks', 'detail', id],
    queryFn: () => getTask(id),
    enabled: Number.isFinite(id),
  });

  const retryMutation = useMutation({
    mutationFn: retryTask,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', id] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['statuses'] });
    },
  });

  const [regenStage, setRegenStage] = useState<Stage | ''>('');
  const regenMutation = useMutation({
    mutationFn: () =>
      regenerateTask(id, regenStage ? { from_stage: regenStage } : {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', id] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['statuses'] });
      setRegenStage('');
    },
  });

  if (!Number.isFinite(id)) {
    return <div className="text-sm text-destructive">{t('errors:badRequest')}</div>;
  }
  if (taskQuery.isLoading) return <div className="text-sm text-muted-foreground">…</div>;
  if (taskQuery.isError || !taskQuery.data) {
    return <div className="text-sm text-destructive">{t('errors:notFound')}</div>;
  }
  const task = taskQuery.data;
  const isFailed = (FAILURE_STATUSES as readonly string[]).includes(task.status);

  return (
    <div className="flex flex-col gap-4">
      <Link to="/tasks" className="text-xs text-muted-foreground hover:underline">
        {t('detail.back')}
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold">
            📞 {task.num_a ?? '—'} → {task.num_b ?? '—'}
          </h2>
          <StatusBadge status={task.status} />
        </div>
        <div className="flex items-center gap-2">
          <select
            className="h-8 rounded-md border bg-background px-2 text-xs"
            value={regenStage}
            onChange={(e) => setRegenStage(e.target.value as Stage | '')}
            aria-label={t('detail.regenerate')}
          >
            <option value="">auto</option>
            <option value="download">download</option>
            <option value="transcribe">transcribe</option>
            <option value="title">title</option>
            <option value="jira">jira</option>
          </select>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={regenMutation.isPending}
            onClick={() => regenMutation.mutate()}
          >
            {t('detail.regenerate')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!isFailed || retryMutation.isPending}
            title={!isFailed ? t('detail.retryDisabled') : undefined}
            onClick={() => retryMutation.mutate(id)}
          >
            {t('actions.retry')}
          </Button>
        </div>
      </div>

      <section className="rounded-md border bg-card p-4 text-xs">
        <h3 className="mb-2 text-sm font-medium">{t('detail.metadata')}</h3>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-3">
          <div>
            <span className="text-muted-foreground">id</span> {task.id}
          </div>
          <div className="truncate">
            <span className="text-muted-foreground">{t('detail.session')}</span> {task.session_id}
          </div>
          <div>
            <span className="text-muted-foreground">{t('detail.direction')}</span>{' '}
            {task.direction ?? '—'}
          </div>
          <div>
            <span className="text-muted-foreground">{t('detail.started')}</span>{' '}
            {formatDateTime(task.call_started_at)}
          </div>
          <div>
            <span className="text-muted-foreground">{t('detail.duration')}</span>{' '}
            {formatDuration(task.duration_seconds)}
          </div>
          <div>
            <span className="text-muted-foreground">tasks</span> {task.tasks}
          </div>
        </div>
      </section>

      {task.last_error && (
        <section className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-xs">
          <h3 className="mb-2 text-sm font-medium">{t('detail.lastError')}</h3>
          <pre className="whitespace-pre-wrap break-words text-destructive">{task.last_error}</pre>
          <div className="mt-1 text-muted-foreground">{formatDateTime(task.last_error_at)}</div>
        </section>
      )}

      {task.media_path && (
        <section className="rounded-md border bg-card p-4 text-xs">
          <h3 className="mb-2 text-sm font-medium">{t('detail.audio')}</h3>
          <div className="text-muted-foreground">{task.media_path}</div>
          {task.record_url && (
            <a
              href={task.record_url}
              className="text-primary underline"
              target="_blank"
              rel="noreferrer"
            >
              record_url
            </a>
          )}
        </section>
      )}

      {task.transcript_payload && (
        <details className="rounded-md border bg-card p-4 text-xs">
          <summary className="cursor-pointer text-sm font-medium">
            {t('detail.transcript')}
          </summary>
          <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">
            {JSON.stringify(task.transcript_payload, null, 2)}
          </pre>
        </details>
      )}

      <details className="rounded-md border bg-card p-4 text-xs">
        <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          {t('detail.rawSource')} <CopyButton payload={task.source_payload} />
        </summary>
        <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">
          {JSON.stringify(task.source_payload, null, 2)}
        </pre>
      </details>

      {task.transcript_payload && (
        <details className="rounded-md border bg-card p-4 text-xs">
          <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            {t('detail.rawTranscript')} <CopyButton payload={task.transcript_payload} />
          </summary>
          <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">
            {JSON.stringify(task.transcript_payload, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
